import { Button } from "@/components/flexkit/Button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { Input } from "@/components/podkit/forms/Input";
import { InputField } from "@/components/podkit/forms/InputField";
import { PasswordInput } from "@/components/podkit/forms/PasswordInput";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { cn } from "@/components/podkit/lib/cn";
import { useOrganization, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { useCreateSecret, useUpdateSecretValue } from "@/queries/secret-queries";
import { TrackLocations } from "@/hooks/use-segment";
import { formatError } from "@/utils/errors";
import type { PartialMessage, PlainMessage } from "@bufbuild/protobuf";
import type { CrowdStrikeConfig, OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { SecretScope } from "gitpod-next-api/gitpod/v1/secret_pb";
import type { FC, ChangeEvent } from "react";
import { useCallback, useEffect, useState, useRef } from "react";
import { ChevronDown, X } from "lucide-react";

// Docker image regex for validation (reused from DefaultImage.tsx)
const dockerImageRegex =
    /^(?:(?:(?:[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])(?:\.(?:[a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]))*(?::[0-9]+)?\/)?[a-z0-9]+(?:(?:(?:[._]|__|[-]*)[a-z0-9]+)+)?(?:\/[a-z0-9]+(?:(?:(?:[._]|__|[-]*)[a-z0-9]+)+)?)*(?::[a-zA-Z0-9_.-]{1,128})?(?:@[a-z][a-z0-9]*(?:[.-][a-z][a-z0-9]*)*:[a-f0-9]{64})?|[a-z0-9]+(?:[._][a-z0-9]+)*)$/i;

// Falcon sensor tags can only contain alphanumerics, '/', '-', '_'
const falconTagRegex = /^[a-zA-Z0-9/_-]+$/;

type ConfigureCrowdStrikeModalProps = {
    currentConfig?: PlainMessage<CrowdStrikeConfig>;
    onClose: () => void;
};

export const ConfigureCrowdStrikeModal: FC<ConfigureCrowdStrikeModalProps> = ({ currentConfig, onClose }) => {
    const { data: organization } = useOrganization();
    const [cidValue, setCidValue] = useState("");
    const [cidSecretId, setCidSecretId] = useState("");
    const [showMaskedCid, setShowMaskedCid] = useState(false);
    const [image, setImage] = useState("");
    const [tags, setTags] = useState("");
    const [additionalOptions, setAdditionalOptions] = useState<Record<string, string>>({});
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const cidInputRef = useRef<HTMLInputElement>(null);

    // Validation errors
    const [cidError, setCidError] = useState<string | null>(null);
    const [imageError, setImageError] = useState<string | null>(null);
    const [tagsError, setTagsError] = useState<string | null>(null);

    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const createSecret = useCreateSecret();
    const updateSecretValue = useUpdateSecretValue();
    const { toast } = useToast();

    // Initialize form with current config
    useEffect(() => {
        if (currentConfig) {
            setCidSecretId(currentConfig.cidSecretId || "");
            setShowMaskedCid(!!currentConfig.cidSecretId);
            setImage(currentConfig.image || "");
            setTags(currentConfig.tags || "");
            setAdditionalOptions(currentConfig.additionalOptions || {});
        }
    }, [currentConfig]);

    const validateImage = useCallback((imageValue: string): boolean => {
        if (!imageValue.trim()) {
            setImageError("Falcon sensor image is required");
            return false;
        }

        if (!dockerImageRegex.test(imageValue)) {
            setImageError("Expected format: [HOST[:PORT]/]NAMESPACE/REPOSITORY[:TAG]");
            return false;
        }

        setImageError(null);
        return true;
    }, []);

    const validateCid = useCallback(
        (cidVal: string): boolean => {
            if (!cidVal.trim() && !cidSecretId) {
                setCidError("Customer ID is required");
                return false;
            }
            setCidError(null);
            return true;
        },
        [cidSecretId],
    );

    const validateTags = useCallback((tagsValue: string): boolean => {
        if (!tagsValue.trim()) {
            setTagsError(null);
            return true;
        }

        const tagList = tagsValue.split(",").map((t) => t.trim());
        for (const tag of tagList) {
            if (tag && !falconTagRegex.test(tag)) {
                setTagsError("Tags can only contain alphanumerics, '/', '-', and '_'");
                return false;
            }
        }

        setTagsError(null);
        return true;
    }, []);

    const handleSave = useCallback(async () => {
        // Validate all fields
        const isCidValid = validateCid(cidValue);
        const isImageValid = validateImage(image);
        const isTagsValid = validateTags(tags);

        if (!isCidValid || !isImageValid || !isTagsValid) {
            return;
        }

        try {
            let secretId = cidSecretId;

            // Handle CID secret creation/update if value changed
            if (cidValue.trim()) {
                if (!organization?.id) {
                    throw new Error("Organization ID not available");
                }

                // True upsert: try to update first, if it fails (secret deleted), create new
                if (secretId) {
                    try {
                        await updateSecretValue.mutateAsync({
                            secretId,
                            plaintextValue: cidValue,
                        });
                    } catch {
                        // Secret doesn't exist anymore, create a new one
                        console.log("Secret not found, creating new one");
                        const createdSecret = await createSecret.mutateAsync({
                            scope: new SecretScope({
                                scope: {
                                    value: organization.id,
                                    case: "organizationId",
                                },
                            }),
                            secretName: "crowdstrike_falcon_cid",
                            plaintextValue: cidValue,
                            mount: {
                                case: "apiOnly",
                                value: true,
                            },
                        });
                        secretId = createdSecret?.id;
                    }
                } else {
                    // No secret ID, create new secret
                    const createdSecret = await createSecret.mutateAsync({
                        scope: new SecretScope({
                            scope: {
                                value: organization.id,
                                case: "organizationId",
                            },
                        }),
                        secretName: "crowdstrike_falcon_cid",
                        plaintextValue: cidValue,
                        mount: {
                            case: "apiOnly",
                            value: true,
                        },
                    });
                    secretId = createdSecret?.id;
                }
            }

            // Update organization policies with all config
            const params: PartialMessage<OrganizationPolicies> = {
                securityAgentPolicy: {
                    crowdstrike: {
                        cidSecretId: secretId,
                        image,
                        tags,
                        additionalOptions,
                    },
                },
            };

            await updateOrganizationPolicies.mutateAsync(params);

            toast({
                title: "CrowdStrike settings saved",
                description: "Configuration has been updated successfully",
            });

            onClose();
        } catch (error) {
            console.error(error);

            // Map secret-related errors to more user-friendly messages
            let errorMessage = formatError(error);
            const errorString = String(error);

            if (errorString.includes("pattern") || errorString.includes("name")) {
                errorMessage = "Failed to save CrowdStrike configuration. Please check that all fields are valid.";
            } else if (errorString.includes("secret")) {
                errorMessage = "Failed to store Customer ID securely. Please try again.";
            }

            toast({
                title: "Failed to save CrowdStrike settings",
                description: errorMessage,
            });
        }
    }, [
        cidValue,
        cidSecretId,
        image,
        tags,
        additionalOptions,
        organization?.id,
        validateCid,
        validateImage,
        validateTags,
        createSecret,
        updateSecretValue,
        updateOrganizationPolicies,
        toast,
        onClose,
    ]);

    const handleAddOption = useCallback(() => {
        const newKey = `option_${Object.keys(additionalOptions).length + 1}`;
        setAdditionalOptions({ ...additionalOptions, [newKey]: "" });
    }, [additionalOptions]);

    const handleRemoveOption = useCallback(
        (key: string) => {
            const newOptions = { ...additionalOptions };
            delete newOptions[key];
            setAdditionalOptions(newOptions);
        },
        [additionalOptions],
    );

    const handleOptionKeyChange = useCallback(
        (oldKey: string, newKey: string) => {
            const newOptions = { ...additionalOptions };
            const value = newOptions[oldKey];
            delete newOptions[oldKey];
            newOptions[newKey] = value;
            setAdditionalOptions(newOptions);
        },
        [additionalOptions],
    );

    const handleOptionValueChange = useCallback(
        (key: string, value: string) => {
            setAdditionalOptions({ ...additionalOptions, [key]: value });
        },
        [additionalOptions],
    );

    const cidPlaceholder = cidSecretId ? "Enter new value to update" : "Enter Customer ID";
    const cidDisplayValue = showMaskedCid ? "••••••••••••••••" : cidValue;

    // Check if form is valid for save button
    const isFormValid = (cidValue.trim() || cidSecretId) && image.trim() && !cidError && !imageError;

    return (
        <Dialog open onOpenChange={(open) => !open && onClose()}>
            <DialogContent
                className="max-w-[600px]"
                data-testid="configure-crowdstrike-modal"
                data-track-location={TrackLocations.ConfigureCrowdStrikeModal}
            >
                <DialogHeader>
                    <DialogTitle>Configure CrowdStrike Falcon</DialogTitle>
                    <DialogDescription>Configure security agent deployment settings</DialogDescription>
                </DialogHeader>

                <DialogBody className="space-y-4">
                    <InputField
                        id="crowdstrike-cid"
                        label={
                            <>
                                Customer ID (CID) <span className="text-content-error">*</span>
                            </>
                        }
                        hint="Your CrowdStrike Customer ID for authentication"
                        error={cidError}
                    >
                        <PasswordInput
                            ref={cidInputRef}
                            id="crowdstrike-cid"
                            data-testid="crowdstrike-cid-input"
                            data-tracking-id-none
                            value={cidDisplayValue}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setCidValue(e.target.value);
                                setCidError(null);
                            }}
                            onFocus={() => {
                                if (showMaskedCid) {
                                    setShowMaskedCid(false);
                                    setCidValue("");
                                }
                            }}
                            onClick={() => {
                                if (showMaskedCid) {
                                    setShowMaskedCid(false);
                                    setCidValue("");
                                }
                            }}
                            onBlur={() => {
                                // Show masked CID again if there's a secret ID and no new value entered
                                if (cidSecretId && !cidValue.trim()) {
                                    setShowMaskedCid(true);
                                }
                            }}
                            placeholder={cidPlaceholder}
                            className={cn(cidError && "border-border-error")}
                            data-tracking-id="default-tracking-id"
                        />
                    </InputField>

                    <InputField
                        id="crowdstrike-image"
                        label={
                            <>
                                Falcon Sensor Image <span className="text-content-error">*</span>
                            </>
                        }
                        hint="Docker image reference for the CrowdStrike Falcon sensor"
                        error={imageError}
                    >
                        <Input
                            id="crowdstrike-image"
                            data-testid="crowdstrike-image-input"
                            type="text"
                            value={image}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                setImage(e.target.value);
                                setImageError(null);
                            }}
                            placeholder="quay.io/crowdstrike/falcon-sensor:latest"
                            className={cn(imageError && "border-border-error")}
                        />
                    </InputField>

                    <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                        <CollapsibleTrigger asChild>
                            <Button
                                variant="ghost"
                                className="flex items-center gap-2 p-2 text-sm font-medium text-content-secondary hover:text-content-primary"
                                data-testid="advanced-options-toggle"
                                data-tracking-id="toggle-crowdstrike-advanced-options-modal"
                            >
                                <ChevronDown
                                    className={cn("h-4 w-4 transition-transform", advancedOpen && "rotate-180")}
                                />
                                Advanced options
                            </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-6 space-y-4">
                            <InputField
                                id="crowdstrike-tags"
                                label="Tags (optional)"
                                hint="Comma-separated tags to apply to the Falcon sensor"
                                error={tagsError}
                            >
                                <Input
                                    id="crowdstrike-tags"
                                    data-testid="crowdstrike-tags-input"
                                    type="text"
                                    value={tags}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                        setTags(e.target.value);
                                        setTagsError(null);
                                    }}
                                    placeholder="ona,production,team/security"
                                />
                            </InputField>

                            <div className="flex flex-col gap-2">
                                <label className="text-base font-normal text-content-primary">
                                    Additional Falcon Options
                                </label>
                                <span className="text-sm text-content-tertiary">
                                    Additional FALCONCTL_OPT_* options as key-value pairs. Keys should NOT include the
                                    FALCONCTL_OPT_ prefix.
                                </span>
                                <div className="flex flex-col gap-2">
                                    {Object.entries(additionalOptions).map(([key, value]) => (
                                        <div key={key} className="flex items-center gap-2">
                                            <Input
                                                type="text"
                                                value={key}
                                                onChange={(e) => handleOptionKeyChange(key, e.target.value)}
                                                placeholder="Key"
                                                className="w-40"
                                                data-testid={`option-key-${key}`}
                                            />
                                            <Input
                                                type="text"
                                                value={value}
                                                onChange={(e) => handleOptionValueChange(key, e.target.value)}
                                                placeholder="Value"
                                                className="flex-1"
                                                data-testid={`option-value-${key}`}
                                            />
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleRemoveOption(key)}
                                                className="h-9 w-9 p-0"
                                                data-testid={`remove-option-${key}`}
                                                data-tracking-id={`remove-option-${key}-modal`}
                                            >
                                                <X className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={handleAddOption}
                                        className="self-start"
                                        data-testid="add-option-button"
                                        data-tracking-id="add-falcon-option-modal"
                                    >
                                        + Add option
                                    </Button>
                                </div>
                            </div>
                        </CollapsibleContent>
                    </Collapsible>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" data-tracking-id="cancel-crowdstrike-settings">
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button
                        onClick={handleSave}
                        disabled={!isFormValid}
                        data-testid="save-crowdstrike-settings"
                        data-tracking-id="save-crowdstrike-settings"
                    >
                        Save Settings
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
